import { forwardRef } from 'react';

import { Select } from '../../inputs/Select/Select';
import type { SelectOption } from '../../inputs/Select/SelectOption/SelectOption';
import { DefaultFormFieldLayout } from '../DefaultFormFieldLayout/DefaultFormFieldLayout';
import type { HelperText } from '../FormFieldHelperText/FormFieldHelperText';
import {
  getHelperTextProps,
  HelperTextPopover,
} from '../FormFieldHelperText/FormFieldHelperText';

export type CoreFormFieldSelectProps<T> = {
  inputProps?: {
    id?: string;
    name?: string;
  };
  label: React.ReactNode;
  options: SelectOption<T>[];
  value?: SelectOption<T>;
  onChange: (val: SelectOption<T>) => void;
  error?: string;
  helperText?: HelperText;
  inline?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

export const CoreFormFieldSelect = forwardRef(function _CoreFormFieldSelect<T>(
  {
    inputProps,
    label,
    options,
    value,
    onChange,
    error,
    helperText,
    inline,
    placeholder,
    disabled,
  }: CoreFormFieldSelectProps<T>,
  _ref: React.Ref<HTMLButtonElement>
) {
  const { popoverProps } = getHelperTextProps({
    helperText,
    label,
  });

  return (
    <DefaultFormFieldLayout
      label={label}
      error={error}
      inputId={inputProps?.id}
      inline={inline}
      helperText={helperText}
    >
      <div className="flex items-center gap-2" data-testid={inputProps?.id}>
        <Select
          id={inputProps?.id}
          // TODO: Modify Select to allow for SelectOption<T>
          selected={value as SelectOption}
          options={options as SelectOption[]}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(newOption?: SelectOption) =>
            typeof onChange === 'function' &&
            onChange(newOption as SelectOption<T>)
          }
          error={error}
        />
        {popoverProps && <HelperTextPopover {...popoverProps} />}
      </div>
    </DefaultFormFieldLayout>
  );
  // Casting to ensure generic gets inferred correctly
  // See https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
}) as <T>(
  props: CoreFormFieldSelectProps<T> & React.RefAttributes<HTMLButtonElement>
) => React.ReactElement;
